import { Physique, createRng, nextInt, sinDeg, restLiquids, wakeAll, TRIG_ONE, type World } from '@invaders/sim';
import { SEA_LEVEL_FROM_BOTTOM } from './state';

const { EMPTY, ROCK, SAND, WATER } = Physique;

/** Rise of the coast slope, in cells, before the island reaches its full relief. */
const COAST_RAMP = 80;

/**
 * An island (GDD section 10): sea at both ends, sea surface SEA_LEVEL_FROM_BOTTOM above the bottom,
 * rolling rock relief, sand beaches. Integer-only, so identical in every browser.
 */
export function generateIsland(world: World, seed: number): void {
  const { w, h, data } = world.physique;
  const rng = createRng(seed);
  const seaY = h - SEA_LEVEL_FROM_BOTTOM;
  const coastL = Math.trunc((w * 12) / 100);
  const coastR = Math.trunc((w * 88) / 100);
  const p1 = nextInt(rng, 360);
  const p2 = nextInt(rng, 360);
  const p3 = nextInt(rng, 360);
  data.fill(EMPTY);

  for (let x = 0; x < w; x++) {
    const c = Math.min(x - coastL, coastR - x); // signed distance to the nearest coast, > 0 on land
    let elev: number;
    if (c >= 0) {
      const relief = 70 +
        Math.trunc((40 * sinDeg(Math.trunc((x * 360 * 2) / w) + p1)) / TRIG_ONE) +
        Math.trunc((14 * sinDeg(Math.trunc((x * 360 * 7) / w) + p2)) / TRIG_ONE) +
        Math.trunc((4 * sinDeg(Math.trunc((x * 360 * 23) / w) + p3)) / TRIG_ONE);
      elev = Math.trunc((Math.min(c, COAST_RAMP) * relief) / COAST_RAMP);
    } else {
      elev = Math.max(Math.trunc((c * 3) / 4), -(SEA_LEVEL_FROM_BOTTOM - 30));
    }
    const surface = seaY - elev;
    for (let y = Math.max(0, surface); y < h; y++) data[y * w + x] = ROCK;
    if (c > -60 && c < 50) { // beach
      const depth = 6 - Math.trunc(Math.abs(c) / 10);
      for (let y = Math.max(0, surface - depth); y < surface; y++) data[y * w + x] = SAND;
    }
  }
  for (let y = seaY; y < h; y++) for (let x = 0; x < w; x++) if (data[y * w + x] === EMPTY) data[y * w + x] = WATER;
  world.cellState.data.fill(0);
  restLiquids(world, 0, 0, w - 1, h - 1);
  wakeAll(world);
}
